import { toast } from 'react-toastify'

export type ToastType = 'success' | 'error' | 'warning'

interface ToastOptions {
  message: string
  duration?: number
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

/**
 * Affiche un toast de succès
 */
export const showSuccessToast = (options: ToastOptions) => {
  const { message, duration = 5000, position = 'top-right' } = options
  
  toast.success(message, {
    position,
    autoClose: duration,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    className: 'toast-success',
  })
}

/**
 * Affiche un toast d'erreur
 */
export const showErrorToast = (options: ToastOptions) => {
  const { message, duration = 7000, position = 'top-right' } = options
  
  toast.error(message, {
    position,
    autoClose: duration,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    className: 'toast-error',
  })
}

/**
 * Affiche un toast d'avertissement
 */
export const showWarningToast = (options: ToastOptions) => {
  const { message, duration = 6000, position = 'top-right' } = options
  
  toast.warning(message, {
    position,
    autoClose: duration,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    className: 'toast-warning',
  })
}

/**
 * Fonction générique pour afficher un toast selon le type
 */
export const showToast = (type: ToastType, options: ToastOptions) => {
  switch (type) {
    case 'success':
      showSuccessToast(options)
      break
    case 'error':
      showErrorToast(options)
      break
    case 'warning':
      showWarningToast(options)
      break
    default:
      showSuccessToast(options)
  }
}

/**
 * Fonctions de raccourci pour les cas d'usage courants
 */
export const toastUtils = {
  // Méthodes génériques
  success: (message: string) => showSuccessToast({ message }),
  error: (message: string) => showErrorToast({ message }),
  
  // Succès
  loginSuccess: () => showSuccessToast({ message: 'Connexion réussie !' }),
  registerSuccess: () => showSuccessToast({ message: "Inscription réussie ! Vérifiez votre email pour activer votre compte." }),
  logoutSuccess: () => showSuccessToast({ message: 'Déconnexion réussie !' }),
  saveSuccess: () => showSuccessToast({ message: 'Sauvegarde réussie !' }),
  updateSuccess: () => showSuccessToast({ message: 'Mise à jour réussie !' }),
  deleteSuccess: () => showSuccessToast({ message: 'Suppression réussie !' }),
  
  // Erreurs
  loginError: (message?: string) => showErrorToast({ 
    message: message || 'Erreur de connexion' 
  }),
  networkError: () => showErrorToast({ 
    message: 'Erreur de connexion au serveur' 
  }),
  // NOTE: une seule clé validationError pour éviter les doublons
  validationError: (message?: string) => showErrorToast({ 
    message: message || 'Erreur de validation des données' 
  }),
  serverError: () => showErrorToast({ 
    message: 'Erreur du serveur' 
  }),
  
  // Avertissements
  sessionExpired: () => showWarningToast({ 
    message: 'Session expirée, veuillez vous reconnecter' 
  }),
  unsavedChanges: () => showWarningToast({ 
    message: 'Vous avez des modifications non sauvegardées' 
  }),
  invalidCredentials: () => showWarningToast({ 
    message: 'Identifiants invalides' 
  }),
  maintenanceMode: () => showWarningToast({ 
    message: 'Service temporairement en maintenance. Veuillez réessayer plus tard.',
    duration: 10000
  }),
  
  // Erreurs spécifiques aux codes de statut
  tokenExpiredError: () => showWarningToast({ 
    message: 'Votre session a expiré, reconnexion automatique...' 
  }),
}
